import React, { useState, useEffect, useRef } from 'react';
import {
  MessageCircle, Users, Play, Square,
  ChevronUp, ChevronDown, Zap,
  Loader, BarChart3, RotateCcw, History, Plus,
  Eye, EyeOff, Clock, Lock, Unlock, ArrowLeft, ExternalLink,
  Download, Link, Copy, Check, Star, ArrowUpDown
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ModelLogo from './ModelLogo';
import { motion } from 'framer-motion';
import { trackEvent } from '../utils/GoogleAnalytics';
// useModels not needed - loading directly from /group-chat/models
import { showToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// CONSTANTS
// ==========================================
const MIN_MODELS = 2;
const MAX_MODELS = 5;
const MIN_CREDITS = 5;

// Detect if text is RTL (Hebrew/Arabic)
const isRTL = (text) => {
  if (!text) return false;
  // Check first 100 chars for Hebrew/Arabic characters
  const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
  return rtlChars.test(text.substring(0, 100));
};

// Format relative time
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ==========================================
// HELPER COMPONENTS
// ==========================================

function TypingIndicator({ modelName }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
        <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
      </div>
      <span className="font-medium">{modelName} is typing...</span>
    </div>
  );
}

// Parse and render rich text (bold, italic)
function renderRichText(text) {
  if (!text) return null;

  // First handle **bold**, then *italic*
  const result = [];
  let key = 0;

  // Split by **bold** pattern first
  const boldParts = text.split(/(\*\*[^*]+\*\*)/g);

  boldParts.forEach((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Bold text
      const boldContent = part.slice(2, -2);
      result.push(
        <strong key={key++} className="font-bold text-gray-900">{boldContent}</strong>
      );
    } else if (part.includes('*')) {
      // Check for *italic* within this part
      const italicParts = part.split(/(\*[^*]+\*)/g);
      italicParts.forEach((iPart) => {
        if (iPart.startsWith('*') && iPart.endsWith('*') && iPart.length > 2) {
          result.push(
            <em key={key++} className="italic">{iPart.slice(1, -1)}</em>
          );
        } else if (iPart) {
          result.push(iPart);
        }
      });
    } else if (part) {
      result.push(part);
    }
  });

  return result.length > 0 ? result : text;
}

function ChatMessage({ message, isAlternate = false }) {
  // Handle system messages (join/leave/topic)
  if (message.type === 'system') {
    // Special styling for topic announcement
    if (message.isTopic) {
      return (
        <div className="flex justify-center my-4">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl shadow-lg max-w-md">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{message.icon || '📝'}</span>
              <span className="font-semibold text-sm opacity-90">Discussion Topic</span>
            </div>
            <p className="font-medium" dir={isRTL(message.content) ? 'rtl' : 'ltr'}>
              {message.content}
            </p>
          </div>
        </div>
      );
    }

    // Regular system message (join/leave)
    return (
      <div className="flex justify-center my-3">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
          message.error
            ? 'bg-red-50 text-red-600 border border-red-200'
            : 'bg-blue-50 text-blue-600 border border-blue-200'
        }`}>
          <span>{message.icon || '💬'}</span>
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  const textDirection = isRTL(message.content) ? 'rtl' : 'ltr';
  const textAlign = isRTL(message.content) ? 'text-right' : 'text-left';

  const bgColor = isAlternate ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200';
  const alignment = isAlternate ? 'ml-4 sm:ml-12' : 'mr-4 sm:mr-12';

  return (
    <div className={`${alignment} mb-4`}>
      {/* Header */}
      <div className={`flex items-center gap-2 mb-1.5 ${isAlternate ? 'justify-end' : ''}`}>
        {!isAlternate && <ModelLogo modelName={message.name} size="sm" />}
        <span className="text-sm font-semibold text-gray-800">{message.name}</span>
        <span className="text-xs text-gray-400">{message.latency_ms ? `${(message.latency_ms / 1000).toFixed(1)}s` : ''}</span>
        {isAlternate && <ModelLogo modelName={message.name} size="sm" />}
      </div>

      {/* Bubble */}
      <div className={`${bgColor} rounded-2xl px-4 py-3 shadow-sm border`} dir={textDirection}>
        <p className={`text-base text-gray-700 whitespace-pre-wrap leading-relaxed ${textAlign}`}>
          {renderRichText(message.content)}
        </p>
      </div>

      {/* Footer */}
      <div className={`flex items-center gap-2 mt-1.5 text-xs text-gray-400 ${isAlternate ? 'justify-end' : ''}`}>
        <span>{message.tokens} tokens</span>
        <span>•</span>
        <span>{message.cost?.toFixed(2)} cr</span>
      </div>
    </div>
  );
}

function StatsPanel({ stats, models }) {
  if (!stats) return null;

  const modelsList = Object.entries(stats.per_model || {});

  // Find best for each metric (just for highlighting, not declaring "winner")
  const fastestModel = modelsList.reduce((best, [id, data]) => {
    if (!best || (data.avg_latency_ms && data.avg_latency_ms < best.latency)) {
      return { id, latency: data.avg_latency_ms };
    }
    return best;
  }, null)?.id;

  const cheapestModel = modelsList.reduce((best, [id, data]) => {
    if (!best || data.cost < best.cost) {
      return { id, cost: data.cost };
    }
    return best;
  }, null)?.id;

  const getCellStyle = (isBest) => {
    if (isBest) return 'bg-blue-100 text-blue-700';
    return 'text-gray-600';
  };

  const formatLatency = (ms) => {
    if (!ms) return '-';
    if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
    return ms + 'ms';
  };

  const formatCost = (cost) => {
    if (!cost && cost !== 0) return '-';
    if (cost < 0.01) return '<0.01 cr';
    return cost.toFixed(2) + ' cr';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-sky-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="12" width="4" height="8" rx="1" fill="#10B981" />
              <rect x="10" y="6" width="4" height="14" rx="1" fill="#F472B6" />
              <rect x="16" y="2" width="4" height="18" rx="1" fill="#60A5FA" />
            </svg>
            Chat Statistics
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">{stats.total_messages} messages</span>
            <span className="text-gray-500">{Math.round(stats.duration_seconds)}s</span>
            <span className="font-semibold text-blue-600">{formatCost(stats.total_cost)}</span>
          </div>
        </div>
      </div>

      {/* Stats Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="py-2 px-4 text-left font-semibold text-gray-600">Model</th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={14} className="text-amber-500" />
                  <span className="text-xs text-gray-500 font-semibold">AVG SPEED</span>
                </div>
              </th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={14} className="text-yellow-500" />
                  <span className="text-xs text-gray-500 font-semibold">CREDITS</span>
                </div>
              </th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <MessageCircle size={14} className="text-blue-500" />
                  <span className="text-xs text-gray-500 font-semibold">MSGS</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {modelsList.map(([modelId, data], index) => (
              <tr
                key={modelId}
                className={`border-b border-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <ModelLogo modelName={data.name} size="xs" />
                    <span className="font-medium text-gray-700">{data.name}</span>
                  </div>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`font-bold px-2 py-0.5 rounded ${getCellStyle(modelId === fastestModel)}`}>
                    {formatLatency(data.avg_latency_ms)}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`font-bold px-2 py-0.5 rounded ${getCellStyle(modelId === cheapestModel)}`}>
                    {formatCost(data.cost)}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="font-bold text-gray-600">{data.messages}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transparency note */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-400 text-center">
          ⚖️ All models received the exact same prompt and rules. No custom tuning.
        </p>
      </div>
    </div>
  );
}

// ==========================================
// HISTORY CARD - For chat history list
// ==========================================
function HistoryCard({ chat, onClick, onTogglePublic, onVote, isOwner }) {
  const models = chat.models || [];
  const messages = chat.messages || [];
  const [isHovered, setIsHovered] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);

  // Animation on hover
  useEffect(() => {
    if (!isHovered || messages.length === 0) {
      setVisibleMessages(0);
      return;
    }

    setVisibleMessages(1);
    const interval = setInterval(() => {
      setVisibleMessages(prev => prev < messages.length ? prev + 1 : prev);
    }, 600);

    return () => clearInterval(interval);
  }, [isHovered, messages.length]);

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden relative h-[220px]"
      onClick={() => onClick(chat.id)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Header accent */}
      <div className="h-1 bg-gradient-to-r from-blue-400 to-sky-400 absolute top-0 left-0 right-0 z-20" />

      {/* Action Buttons - Always visible, on top of everything */}
      <div className="absolute top-3 right-3 z-30 flex items-center gap-1.5">
        {/* Vote button */}
        {onVote && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVote(chat.id);
            }}
            className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
              chat.user_voted
                ? 'bg-yellow-100 text-yellow-600'
                : 'bg-gray-100 hover:bg-yellow-50 text-gray-400 hover:text-yellow-500'
            }`}
            title={chat.user_voted ? 'You voted!' : 'Vote for this debate'}
          >
            <Star size={14} fill={chat.user_voted ? 'currentColor' : 'none'} />
          </button>
        )}

        {/* Public/Private toggle */}
        {isOwner && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePublic(chat.id);
            }}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0 bg-gray-100 hover:bg-gray-200"
            title={chat.is_public ? 'Public - click to make private' : 'Private - click to make public'}
          >
            {chat.is_public ? <Unlock size={14} className="text-green-600" /> : <Lock size={14} className="text-gray-500" />}
          </button>
        )}
      </div>

      {/* Hover Animation Overlay */}
      {isHovered && messages.length > 0 && (
        <div className="absolute inset-0 bg-white z-10 overflow-y-auto p-3 pt-4">
          {messages.slice(0, visibleMessages).map((msg, i) => (
            <div
              key={i}
              className={`mb-2 flex items-start gap-2 animate-fadeIn ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}
            >
              <ModelLogo modelName={msg.model_name} size="sm" />
              <div className={`flex-1 min-w-0 ${i % 2 === 1 ? 'text-right' : ''}`}>
                <p className="text-xs font-semibold text-gray-700 truncate">{msg.model_name}</p>
                <p className="text-xs text-gray-600 line-clamp-2">{msg.content}</p>
              </div>
            </div>
          ))}
          {visibleMessages < messages.length && (
            <div className="flex items-center gap-1 text-blue-500 mt-2">
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" />
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          )}
        </div>
      )}

      {/* Static Content */}
      <div className={`h-full flex flex-col pt-1 transition-opacity ${isHovered && messages.length > 0 ? 'opacity-0' : 'opacity-100'}`}>
        <div className="p-4 flex flex-col flex-1">
          {/* Topic - without the toggle button (moved to top right) */}
          <div className="flex items-start justify-between gap-2 mb-3 pr-16">
            <h3
              className="font-bold text-gray-900 line-clamp-2 flex-1 text-sm"
              dir={isRTL(chat.topic) ? 'rtl' : 'ltr'}
            >
              {chat.topic}
            </h3>
          </div>

          {/* Models with logos */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {models.slice(0, 4).map((model, i) => (
              <div
                key={i}
                className="flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1"
              >
                <ModelLogo modelName={model.name || model} size="xs" />
                <span className="text-xs text-gray-700 font-medium truncate max-w-[50px]">
                  {(model.name || model).split(' ')[0]}
                </span>
              </div>
            ))}
            {models.length > 4 && (
              <span className="text-xs text-gray-400 ml-1">+{models.length - 4}</span>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1" />
        </div>

        {/* Footer - fixed at bottom */}
        <div className="flex items-center justify-between text-xs text-gray-400 px-4 py-2.5 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <MessageCircle size={12} />
              {chat.total_messages}
            </span>
            {(chat.votes || 0) > 0 && (
              <span className="flex items-center gap-1 text-yellow-600">
                <Star size={12} fill="currentColor" />
                {chat.votes}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {chat.duration_seconds}s
            </span>
          </div>
          <span>{formatTimeAgo(chat.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// CHAT VIEWER - Full chat view
// ==========================================
function ChatViewer({ chatId, onBack, user }) {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadChat();
  }, [chatId]);

  const loadChat = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API_URL}/group-chat/view/${chatId}`, { headers });
      if (!response.ok) throw new Error('Failed to load chat');

      const data = await response.json();
      setChat(data);
    } catch (err) {
      showToast('Failed to load chat', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublic = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/view/${chatId}/toggle-public`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setChat(prev => ({ ...prev, is_public: data.is_public }));
        showToast(data.is_public ? 'Chat is now public' : 'Chat is now private', 'success');
      }
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}/chat/${chatId}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      showToast('Link copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showToast('Failed to copy link', 'error');
    }
  };

  const [videoGenerating, setVideoGenerating] = useState(false);

  const handleDownloadVideo = async () => {
    if (videoGenerating) return;

    setVideoGenerating(true);
    showToast('🎬 Generating video... This may take a minute', 'info');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/view/${chatId}/export-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `debate_${chatId}.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showToast('✅ Video downloaded!', 'success');
      } else {
        const data = await response.json();
        showToast(data.detail || 'Failed to generate video', 'error');
      }
    } catch (err) {
      console.error('Video generation error:', err);
      showToast('Failed to generate video', 'error');
    } finally {
      setVideoGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="text-center py-16 text-gray-500">
        Chat not found
        <button onClick={onBack} className="block mx-auto mt-4 text-blue-600 hover:underline">
          Go back
        </button>
      </div>
    );
  }

  const models = chat.models || [];

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors py-1"
      >
        <ArrowLeft size={18} />
        Back to History
      </button>

      {/* Chat Header - Blue colors */}
      <div className="bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg mb-2" dir={isRTL(chat.topic) ? 'rtl' : 'ltr'}>{chat.topic}</h2>

            {/* Models with logos */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {models.map((model, i) => (
                <div key={i} className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1">
                  <ModelLogo modelName={model.name || model} size="xs" />
                  <span className="text-sm font-medium">{(model.name || model).split(' ')[0]}</span>
                </div>
              ))}
            </div>

            <div className="text-xs opacity-80">
              by {chat.user} • {formatTimeAgo(chat.created_at)} • {chat.total_messages} messages
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Share button */}
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              title="Copy share link"
            >
              {copied ? <Check size={16} /> : <Link size={16} />}
              <span className="text-sm hidden sm:inline">Share</span>
            </button>

            {/* Video download button - Pro+ only */}
            {user && ['pro', 'premium', 'admin'].includes(user.plan_tier) ? (
              <button
                onClick={handleDownloadVideo}
                disabled={videoGenerating}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                  videoGenerating
                    ? 'bg-yellow-500/50 cursor-wait'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
                title="Download as Video"
              >
                {videoGenerating ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Download size={16} />
                )}
                <span className="text-sm hidden sm:inline">
                  {videoGenerating ? 'Creating...' : 'Video'}
                </span>
              </button>
            ) : (
              <button
                onClick={() => showToast('🎬 Video export is a Pro feature', 'info')}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors bg-white/10 opacity-60"
                title="Video export - Pro feature"
              >
                <Download size={16} />
                <span className="text-sm hidden sm:inline">Video</span>
                <span className="text-[10px] bg-yellow-500 text-black px-1 rounded font-bold">PRO</span>
              </button>
            )}

            {/* Public/Private toggle for owner */}
            {chat.is_owner && (
              <button
                onClick={handleTogglePublic}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors bg-white/20 hover:bg-white/30"
                title={chat.is_public ? 'Make Private' : 'Make Public'}
              >
                {chat.is_public ? <Unlock size={16} className="text-green-300" /> : <Lock size={16} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-gray-50 rounded-xl p-4 max-h-[500px] overflow-y-auto border border-gray-200">
        {chat.messages?.map((msg, index) => (
          <ChatMessage key={msg.id} message={msg} isAlternate={index % 2 === 1} />
        ))}
      </div>

      {/* Stats */}
      {chat.per_model_stats && (
        <StatsPanel stats={{
          total_messages: chat.total_messages,
          total_cost: chat.total_cost,
          total_tokens: chat.total_tokens,
          duration_seconds: chat.duration_seconds,
          per_model: chat.per_model_stats
        }} />
      )}

      {/* Make Public prompt - animated lock */}
      {chat.is_owner && !chat.is_public && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <motion.div
                className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
              >
                <Lock size={20} className="text-blue-600" />
              </motion.div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">Share this debate?</h4>
                <p className="text-sm text-gray-600">Make it public so others can see and vote</p>
              </div>
            </div>
            <motion.button
              onClick={handleTogglePublic}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-md"
            >
              <Unlock size={16} />
              <span>Make Public</span>
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Already Public state */}
      {chat.is_owner && chat.is_public && (
        <div className="mt-4 bg-green-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Unlock size={20} className="text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm">This debate is public ✨</h4>
                <p className="text-sm text-gray-500">Anyone with the link can see and vote</p>
              </div>
            </div>
            <button
              onClick={handleTogglePublic}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-600 border border-gray-200 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors"
            >
              <Lock size={16} />
              <span>Make Private</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// MODEL SELECTOR - Multi-Select with Checkboxes
// ==========================================
function ModelSelector({ selectedModels, onToggle, onReorder, availableModels, maxModels, minModels }) {
  const [expandedProvider, setExpandedProvider] = React.useState(null);

  const isSelected = (modelId) => selectedModels.some(m => m.id === modelId);

  const handleToggle = (model) => {
    // PORTFOLIO: locked check removed — allow selecting any model in demo mode
    if (isSelected(model.id)) {
      onToggle(selectedModels.filter(m => m.id !== model.id));
    } else {
      if (selectedModels.length >= maxModels) {
        showToast(`Maximum ${maxModels} models allowed`, 'warning');
        return;
      }
      onToggle([...selectedModels, model]);
    }
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...selectedModels];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    onReorder(newOrder);
  };

  const moveDown = (index) => {
    if (index === selectedModels.length - 1) return;
    const newOrder = [...selectedModels];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    onReorder(newOrder);
  };

  // Group by provider
  const grouped = {};
  availableModels.forEach(model => {
    const provider = model.provider || 'Other';
    if (!grouped[provider]) grouped[provider] = [];
    grouped[provider].push(model);
  });
  const providers = Object.keys(grouped).sort((a, b) => {
    const aHas = grouped[a].some(m => isSelected(m.id));
    const bHas = grouped[b].some(m => isSelected(m.id));
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      {/* Provider Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {providers.map(provider => {
          const providerModels = grouped[provider];
          const selectedCount = providerModels.filter(m => isSelected(m.id)).length;
          const isExpanded = expandedProvider === provider;

          return (
            <button
              key={provider}
              onClick={() => setExpandedProvider(isExpanded ? null : provider)}
              className={`relative flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all text-left ${
                isExpanded
                  ? 'bg-blue-50 border-blue-400 shadow-md'
                  : selectedCount > 0
                    ? 'bg-white border-green-400 shadow-sm'
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              <ModelLogo modelName={providerModels[0]?.name || provider} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">{provider}</div>
                <div className="text-xs text-gray-400">{providerModels.length} model{providerModels.length > 1 ? 's' : ''}</div>
              </div>
              {selectedCount > 0 && (
                <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                  {selectedCount}
                </span>
              )}
              <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
            </button>
          );
        })}
      </div>

      {/* Expanded Models Panel */}
      {expandedProvider && grouped[expandedProvider] && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-3 space-y-1.5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{expandedProvider} Models</span>
          </div>
          {grouped[expandedProvider].sort((a, b) => (b.flagship_score || 0) - (a.flagship_score || 0)).map(model => {
            const selected = isSelected(model.id);
            const selectedIndex = selectedModels.findIndex(m => m.id === model.id);

            return (
              <button
                key={model.id}
                onClick={() => handleToggle(model)}
                disabled={false}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition-all text-left ${
                  selected ? 'bg-white shadow-sm' : 'hover:bg-white/80'
                }`}
              >
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                  selected ? 'bg-green-500 border-transparent text-white' : 'border-gray-300 bg-white'
                }`}>
                  {selected && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <ModelLogo modelName={model.name} size="sm" />
                <div className="flex-1 min-w-0">
                  <span className={`font-medium text-sm ${model.locked ? 'text-gray-400' : 'text-gray-900'}`}>{model.name}</span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{model.credits_per_run || '?'} cr</span>
                {model.locked && <span className="text-orange-500 flex-shrink-0">🔒</span>}
                {selected && (
                  <span className="bg-green-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                    {selectedIndex + 1}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Selected Count */}
      <div className="flex items-center justify-between text-sm">
        <span className={`${selectedModels.length >= minModels ? 'text-green-600' : 'text-orange-500'}`}>
          {selectedModels.length} of {minModels}-{maxModels} models selected
        </span>
        {selectedModels.length > 0 && (
          <button
            onClick={() => onToggle([])}
            className="text-red-500 hover:text-red-600 text-xs"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Selected Models Order */}
      {selectedModels.length > 1 && (
        <div className="border-t border-gray-100 pt-4">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <ArrowUpDown size={18} className="text-blue-500" />
            Speaking Order
          </h2>
          <div className="space-y-1">
            {selectedModels.map((model, index) => (
              <div
                key={model.id}
                className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </span>
                  <span className="text-sm font-medium">{model.name}</span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => moveDown(index)}
                    disabled={index === selectedModels.length - 1}
                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 transition-colors"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================
function GroupChat({ user, onCreditsUpdate }) {
  // URL params for deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab state: 'new' | 'history' | 'view'
  const [activeTab, setActiveTab] = useState('history');
  const [viewingChatId, setViewingChatId] = useState(null);

  // History state
  const [historyChats, setHistoryChats] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all' | 'public' | 'mine'
  const [loadingMore, setLoadingMore] = useState(false);

  // New chat state
  const [selectedModels, setSelectedModels] = useState([]);
  const [topic, setTopic] = useState('');
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [typing, setTyping] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [isChatPublic, setIsChatPublic] = useState(false); // Track if new chat is public

  // Refs
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Load available models - flagship only (one per provider)
  const [chatModels, setChatModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  useEffect(() => {
    const loadChatModels = async () => {
      try {
        // STATIC MODE: load from local JSON file, filter for CHAT category
        const response = await fetch('/data/models.json');
        if (response.ok) {
          const allModels = await response.json();
          const chatModels = allModels.filter(m => m.category === 'CHAT' && m.is_active);
          setChatModels(chatModels);
        }
      } catch (err) {
        console.error('Failed to load chat models:', err);
      } finally {
        setModelsLoading(false);
      }
    };
    loadChatModels();
  }, [user]);

  // Check URL params for deep link to view a chat
  useEffect(() => {
    const viewChatId = searchParams.get('view');
    if (viewChatId) {
      setViewingChatId(viewChatId);
      setActiveTab('view');
      // Clear the param so refresh doesn't keep showing it
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Load history when tab or filter changes
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, user, historyFilter]);

  // Fetch history count on mount so badge shows on Recent button
  useEffect(() => {
    loadHistory();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  // Clear error when user credits change
  useEffect(() => {
    if (user && (user.credits || 0) >= MIN_CREDITS) {
      setError('');
    }
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Load chat history with filter
  const loadHistory = async (reset = true) => {
    if (reset) {
      setHistoryLoading(true);
      setHistoryOffset(0);
    } else {
      setLoadingMore(true);
    }

    const offset = reset ? 0 : historyOffset;
    const limit = 10;
    const token = localStorage.getItem('token');

    try {
      let newChats = [];
      let total = 0;

      // STATIC MODE: load from local JSON file
      const res = await fetch('/data/chats.json');
      if (res.ok) {
        const data = await res.json();
        const allChats = data.chats || [];
        total = allChats.length;
        newChats = allChats.slice(offset, offset + limit);
      }

      if (reset) {
        setHistoryChats(newChats);
      } else {
        setHistoryChats(prev => [...prev, ...newChats]);
      }
      setHistoryTotal(total);
      setHistoryOffset(offset + newChats.length);
      setHistoryHasMore(historyFilter !== 'all' && offset + newChats.length < total);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more history items
  const loadMoreHistory = () => {
    if (!loadingMore && historyHasMore) {
      loadHistory(false);
    }
  };

  // Toggle chat public/private
  const handleTogglePublic = async (chatIdToToggle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/view/${chatIdToToggle}/toggle-public`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistoryChats(prev => prev.map(c =>
          c.id === chatIdToToggle ? { ...c, is_public: data.is_public } : c
        ));
        showToast(data.is_public ? 'Chat is now public' : 'Chat is now private', 'success');
      }
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  // Vote for a chat from the card
  const handleVoteFromCard = async (chatIdToVote) => {
    if (!user) {
      showToast('Please login to vote', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/vote/${chatIdToVote}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHistoryChats(prev => prev.map(c =>
          c.id === chatIdToVote ? { ...c, votes: data.votes, user_voted: data.user_voted } : c
        ));
        showToast(data.user_voted ? '⭐ Voted!' : 'Vote removed', 'success');
        trackEvent('GroupChat', 'Vote', chatIdToVote);
      }
    } catch (err) {
      showToast('Failed to vote', 'error');
    }
  };

  // View a specific chat - navigate to dedicated page
  const handleViewChat = (chatIdToView) => {
    navigate(`/chat/${chatIdToView}`);
  };

  // Start chat
  const handleStart = async () => {
    if (!user) {
      showToast('Please login to start a chat', 'warning');
      return;
    }

    if (selectedModels.length < MIN_MODELS) {
      setError(`Please select at least ${MIN_MODELS} models`);
      return;
    }

    if (!topic.trim()) {
      setError('Please enter a topic');
      return;
    }

    if ((user.credits || 0) < MIN_CREDITS) {
      setError(`Need at least ${MIN_CREDITS} credits to start`);
      return;
    }

    setIsStarting(true);
    setError('');
    setMessages([]);
    setStats(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          topic,
          models: selectedModels.map(m => m.id)
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create chat');
      }

      const data = await response.json();
      setChatId(data.chat_id);
      setIsRunning(true);
      setIsStarting(false);
      trackEvent('GroupChat', 'Start', topic, selectedModels.length);

      // Start streaming
      startStream(data.chat_id);

    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
      setIsStarting(false);
    }
  };

  // Start streaming with fetch (EventSource doesn't work well with auth)
  const startStream = async (chatIdParam) => {
    const token = localStorage.getItem('token');

    // Create abort controller for this stream
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(
        `${API_URL}/group-chat/${chatIdParam}/stream?token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          signal: abortControllerRef.current.signal
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('Stream ended');
          setIsRunning(false);
          setTyping(null);
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('📨 SSE Event:', data.event, data);

              switch (data.event) {
                case 'topic':
                  // Topic announcement - show as first system message
                  setMessages(prev => [...prev, {
                    id: `topic-${Date.now()}`,
                    type: 'system',
                    content: data.content,
                    icon: '📝',
                    isTopic: true
                  }]);
                  break;

                case 'join':
                  // Model joined the chat - show system message
                  setMessages(prev => [...prev, {
                    id: `join-${data.model}-${Date.now()}`,
                    type: 'system',
                    content: `${data.name} joined the chat`,
                    icon: '👋'
                  }]);
                  break;

                case 'leave':
                  // Model left the chat (error) - show system message
                  setTyping(null);
                  setMessages(prev => [...prev, {
                    id: `leave-${data.model}-${Date.now()}`,
                    type: 'system',
                    content: `${data.name} left the chat`,
                    icon: '👋',
                    error: true
                  }]);
                  showToast(`${data.name} disconnected`, 'warning');
                  break;

                case 'typing':
                  setTyping({ model: data.model, name: data.name });
                  break;

                case 'message':
                  setTyping(null);
                  setMessages(prev => [...prev, {
                    id: data.message_id,
                    model: data.model,
                    name: data.name,
                    content: data.content,
                    tokens: data.tokens,
                    cost: data.cost,
                    latency_ms: data.latency_ms
                  }]);
                  break;

                case 'stats':
                  setStats({
                    total_messages: data.total_messages,
                    total_tokens: data.total_tokens,
                    total_cost: data.total_cost,
                    per_model: data.per_model,
                    duration_seconds: data.duration_seconds,
                    winner: data.winner
                  });
                  break;

                case 'error':
                  showToast(data.message, 'error');
                  setIsRunning(false);
                  break;

                case 'end':
                  setIsRunning(false);
                  setTyping(null);
                  // Refresh user credits and history
                  if (onCreditsUpdate) onCreditsUpdate();
                  loadHistory();
                  return;

                default:
                  break;
              }
            } catch (e) {
              console.error('Parse error:', e, line);
            }
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Stream aborted');
      } else {
        console.error('Stream error:', err);
        showToast('Connection lost', 'error');
      }
      setIsRunning(false);
      setTyping(null);
    }
  };

  // Stop chat
  const handleStop = async () => {
    // Abort the stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!chatId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/${chatId}/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Stop error:', err);
    }

    setIsRunning(false);
    setTyping(null);

    // Refresh user credits and history
    if (onCreditsUpdate) onCreditsUpdate();
    loadHistory();
  };

  // Final Round - each model makes closing statement
  const handleFinalRound = async () => {
    // Abort current stream
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!chatId) return;

    const token = localStorage.getItem('token');

    try {
      showToast('🏁 Starting final round...', 'info');

      const eventSource = new EventSource(
        `${API_URL}/group-chat/${chatId}/final-round?token=${encodeURIComponent(token)}`
      );

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.event) {
            case 'final_round_start':
              // Mark that final round started
              break;

            case 'typing':
              setTyping({ model: data.model, name: data.name });
              break;

            case 'message':
              setTyping(null);
              setMessages(prev => [...prev, {
                id: Date.now(),
                model: data.model,
                name: data.name,
                content: `🏁 **Final Statement:**\n${data.content}`,
                tokens: data.tokens,
                cost: data.cost,
                latency_ms: data.latency_ms
              }]);
              break;

            case 'stats':
              setStats({
                total_messages: data.total_messages,
                total_cost: data.total_cost,
                total_tokens: data.total_tokens,
                duration_seconds: data.duration_seconds,
                per_model: data.per_model
              });
              break;

            case 'end':
              eventSource.close();
              setIsRunning(false);
              setTyping(null);
              showToast('🏁 Final round complete!', 'success');
              if (onCreditsUpdate) onCreditsUpdate();
              loadHistory();
              break;

            case 'error':
              console.error('Final round error:', data.error);
              showToast('Error in final round', 'error');
              break;
          }
        } catch (err) {
          console.error('Parse error:', err);
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        setIsRunning(false);
        setTyping(null);
      };

    } catch (err) {
      console.error('Final round error:', err);
      showToast('Failed to start final round', 'error');
    }
  };

  // Reset for new chat
  const handleReset = () => {
    setChatId(null);
    setMessages([]);
    setStats(null);
    setTopic('');
    setSelectedModels([]);
    setError('');
    setTyping(null);
    setIsChatPublic(false);
    setActiveTab('new');
    // Refresh history for next time
    loadHistory();
  };

  // Toggle public/private for newly created chat
  const handleToggleNewChatPublic = async () => {
    if (!chatId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/view/${chatId}/toggle-public`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setIsChatPublic(data.is_public);
        showToast(data.is_public ? '🔓 Chat is now public!' : '🔒 Chat is now private', 'success');
      }
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  // Model selection handler - for multi-select
  const handleModelsChange = (newSelection) => {
    setSelectedModels(newSelection);
    setError('');
  };

  const handleReorderModels = (newOrder) => {
    setSelectedModels(newOrder);
  };

  return (
    <div className="min-h-screen">
      {/* Floating Header with Tabs */}
      {!chatId && activeTab !== 'view' && (
        <div className="px-4 sm:px-0 pb-4">
          <div className="max-w-6xl mx-auto px-0 sm:px-6">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-sm sm:shadow-md overflow-hidden">
              <div className="px-4 sm:px-6 py-4">
                {/* Page Title */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl flex items-center justify-center shadow-md">
                    <MessageCircle size={20} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">AI Group Chat</h1>
                    <p className="text-sm text-gray-500">Watch AI models discuss any topic together</p>
                  </div>
                </div>

                {/* Tabs - History first (default) */}
                <div className="flex gap-2" role="tablist" aria-label="Chat navigation">
                  <button
                    onClick={() => setActiveTab('history')}
                    role="tab"
                    aria-selected={activeTab === 'history'}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 'history'
                        ? 'bg-blue-100 text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <History size={18} />
                    {user ? 'History' : 'Recent'}
                    {historyTotal > 0 && (
                      <span className="bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs">
                        {historyTotal}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('new')}
                    role="tab"
                    aria-selected={activeTab === 'new'}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      activeTab === 'new'
                        ? 'bg-blue-100 text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Plus size={18} />
                    New Chat
                  </button>
                </div>

                {/* Demo mode notice */}
                <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <span className="text-amber-700 text-sm">Demo mode — start is disabled</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 sm:px-0">
      <div className="max-w-6xl mx-auto px-0 sm:px-6 pb-8">
      {/* VIEW TAB - Viewing a specific chat */}
      {activeTab === 'view' && viewingChatId && (
        <ChatViewer
          chatId={viewingChatId}
          onBack={() => {
            setActiveTab('history');
            setViewingChatId(null);
          }}
          user={user}
        />
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && !viewingChatId && (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-100 p-2 shadow-sm">
            <div className="flex items-center gap-1">
              {[
                { id: 'all', label: 'All Results' },
                { id: 'public', label: 'Public' },
                ...(user ? [{ id: 'mine', label: 'My Chats' }] : [])
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setHistoryFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    historyFilter === f.id
                      ? 'bg-blue-500 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {historyTotal > 0 && (
              <span className="text-xs text-gray-400 pr-2">{historyTotal} results</span>
            )}
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader className="animate-spin text-blue-500" size={32} />
            </div>
          ) : historyChats.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <History size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700">
                {historyFilter === 'mine' ? 'No chats yet' : 'No public chats yet'}
              </h3>
              <p className="text-gray-500 mt-1">
                {historyFilter === 'mine' ? 'Start a new chat to see it here' : 'Be the first to start a public chat!'}
              </p>
              <button
                onClick={() => setActiveTab('new')}
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Start New Chat
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {historyChats.map(chat => (
                  <HistoryCard
                    key={chat.id}
                    chat={chat}
                    onClick={handleViewChat}
                    onTogglePublic={handleTogglePublic}
                    onVote={handleVoteFromCard}
                    isOwner={!!user && chat.is_owner !== false}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {historyHasMore && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={loadMoreHistory}
                    disabled={loadingMore}
                    className="px-6 py-3 bg-white border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <>
                        <Loader className="animate-spin" size={18} />
                        Loading...
                      </>
                    ) : (
                      <>
                        Load More ({historyTotal - historyChats.length} remaining)
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* NEW CHAT TAB - Setup Panel - shown when not running */}
      {activeTab === 'new' && !chatId && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          {/* Model Selection */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Users size={18} className="text-blue-500" />
              Select Models ({MIN_MODELS}-{MAX_MODELS})
            </h2>

            {modelsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin text-blue-500" size={24} />
              </div>
            ) : chatModels.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No CHAT models available</p>
                <p className="text-sm mt-1">Please check your database configuration</p>
              </div>
            ) : (
              <ModelSelector
                selectedModels={selectedModels}
                onToggle={handleModelsChange}
                onReorder={handleReorderModels}
                availableModels={chatModels}
                maxModels={MAX_MODELS}
                minModels={MIN_MODELS}
              />
            )}
          </div>

          {/* Topic Input */}
          <div>
            <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <MessageCircle size={18} className="text-blue-500" />
              Discussion Topic
            </h2>
            <textarea
              value={topic}
              onChange={(e) => {
                setTopic(e.target.value);
                setError('');
              }}
              placeholder="Enter a topic for discussion... (e.g., 'Is AI going to replace programmers?' or 'מה העתיד של הבינה המלאכותית?')"
              className="w-full p-4 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              rows={3}
              maxLength={500}
              dir={isRTL(topic) ? 'rtl' : 'ltr'}
            />
            <div className="text-xs text-gray-400 text-right mt-1">{topic.length}/500</div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
              <span>⚠️</span>
              {error}
            </div>
          )}

          {/* Credits Info */}
          <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Zap size={16} className="text-yellow-500" />
              <span>Min {MIN_CREDITS} credits to start • Real-time billing per message</span>
            </div>
            <div className="text-sm font-semibold">
              Your credits: <span className={`${(user?.credits || 0) >= MIN_CREDITS ? 'text-green-600' : 'text-red-500'}`}>{user?.credits || 0}</span>
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={handleStart}
            disabled={true}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-sky-500 text-white rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg"
            title="Demo mode — running disabled"
          >
            <>
              <Play size={20} />
              Start Discussion
            </>
          </button>
        </div>
      )}

      {/* Chat Panel - shown when running or has messages */}
      {chatId && (
        <div className="space-y-4">
          {/* Chat Header - Blue colors */}
          <div className="bg-gradient-to-r from-blue-500 to-sky-500 rounded-xl p-4 text-white shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-lg mb-2" dir={isRTL(topic) ? 'rtl' : 'ltr'}>{topic}</h2>
                {/* Models with logos */}
                <div className="flex flex-wrap items-center gap-2">
                  {selectedModels.map((model, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1">
                      <ModelLogo modelName={model.name} size="xs" />
                      <span className="text-sm font-medium">{model.name.split(' ')[0]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {isRunning ? (
                  <>
                    <button
                      onClick={handleFinalRound}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors font-medium"
                      title="Each model makes a closing statement"
                    >
                      🏁 Final Round
                    </button>
                    <button
                      onClick={handleStop}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 rounded-lg hover:bg-red-600 transition-colors font-medium"
                    >
                      <Square size={16} />
                      Stop
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors font-medium"
                  >
                    <RotateCcw size={16} />
                    New Chat
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-gray-50 rounded-xl p-4 min-h-[400px] max-h-[600px] overflow-y-auto border border-gray-200">
            {messages.length === 0 && !typing && (
              <div className="text-center text-gray-400 py-16">
                <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Waiting for first message...</p>
                <p className="text-sm mt-1">The models will start discussing soon</p>
              </div>
            )}

            {messages.map((msg, index) => (
              <ChatMessage
                key={msg.id || index}
                message={msg}
                isAlternate={index % 2 === 1}
              />
            ))}

            {typing && <TypingIndicator modelName={typing.name} />}

            <div ref={messagesEndRef} />
          </div>

          {/* Running Stats Bar with Controls */}
          {isRunning && messages.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-4 sm:gap-6 text-sm">
                  <div className="flex items-center gap-1.5">
                    <MessageCircle size={16} className="text-blue-500" />
                    <span className="text-gray-500 hidden sm:inline">Messages:</span>
                    {(() => {
                      const aiCount = messages.filter(m => m.type !== 'system').length;
                      return (
                        <>
                          <span className={`font-bold ${aiCount >= 20 ? 'text-amber-600' : 'text-gray-700'}`}>
                            {aiCount}/25
                          </span>
                          {aiCount >= 20 && (
                            <span className="text-xs text-amber-600 hidden sm:inline">(near limit)</span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5">
                    <Zap size={16} className="text-yellow-500" />
                    <span className="text-gray-500">Credits:</span>
                    <span className="font-bold text-green-600">{messages.reduce((sum, m) => sum + (m.cost || 0), 0).toFixed(2)}</span>
                  </div>
                </div>

                {/* Controls - Live + Buttons */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-green-100 rounded-full">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                  <button
                    onClick={handleFinalRound}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-xs sm:text-sm font-medium"
                    title="Each model makes a closing statement"
                  >
                    🏁 <span className="hidden sm:inline">Final</span>
                  </button>
                  <button
                    onClick={handleStop}
                    className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-xs sm:text-sm font-medium"
                  >
                    <Square size={14} />
                    <span className="hidden sm:inline">Stop</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Final Stats */}
          {stats && !isRunning && (
            <StatsPanel stats={stats} models={selectedModels} />
          )}

          {/* Make Public prompt after chat ends */}
          {stats && !isRunning && chatId && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className={`mt-4 rounded-xl p-4 border ${
                isChatPublic
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
              }`}
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <motion.div
                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isChatPublic ? 'bg-green-100' : 'bg-blue-100'
                    }`}
                    animate={!isChatPublic ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                  >
                    {isChatPublic ? (
                      <Unlock size={20} className="text-green-600" />
                    ) : (
                      <Lock size={20} className="text-blue-600" />
                    )}
                  </motion.div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {isChatPublic ? 'This debate is public ✨' : 'Share this debate?'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {isChatPublic
                        ? 'Anyone with the link can see and vote'
                        : 'Make it public so others can discover it'}
                    </p>
                  </div>
                </div>
                <motion.button
                  onClick={handleToggleNewChatPublic}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    isChatPublic
                      ? 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'
                  }`}
                >
                  {isChatPublic ? (
                    <>
                      <Lock size={16} />
                      <span>Make Private</span>
                    </>
                  ) : (
                    <>
                      <Unlock size={16} />
                      <span>Make Public</span>
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>
      )}
      </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default GroupChat;